import React, { useEffect, useState } from 'react';
import { Button, Modal } from '@/components/ui';
import {
  PO_TYPE_LABELS,
  PoConsumption,
  PoConsumptionEffort,
  PurchaseOrder,
  purchaseOrderService,
} from '../../../services/purchaseOrderService';
import { currency } from '../../vendors/utils/format';
import ConsumptionBar from './ConsumptionBar';
import PoConsumptionEffortsTable from './PoConsumptionEffortsTable';

export interface PoConsumptionDetailModalProps {
  show: boolean;
  onHide: () => void;
  po: PurchaseOrder | null;
}

const Stat: React.FC<{
  label: string;
  value: string;
  tone?: 'default' | 'danger';
  hint?: string;
}> = ({ label, value, tone = 'default', hint }) => (
  <div className="tw-min-w-0 tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-px-3 tw-py-3">
    <div className="tw-font-display tw-text-xs tw-font-semibold tw-tracking-wide tw-text-content-muted">
      {label}
    </div>
    <div
      className={`tw-mt-1 tw-truncate tw-font-display tw-text-xl tw-font-semibold tw-tabular-nums ${
        tone === 'danger' ? 'tw-text-danger' : 'tw-text-content'
      }`}
    >
      {value}
    </div>
    {hint && <div className="tw-mt-0.5 tw-text-xs tw-text-content-muted">{hint}</div>}
  </div>
);

function subtitleFor(po: PurchaseOrder): string {
  return [PO_TYPE_LABELS[po.type] ?? po.type, po.vendor?.name, po.description].filter(Boolean).join(' · ');
}

function summaryStats(consumption: PoConsumption) {
  const used = consumption.approved + consumption.submitted;
  const overrun = used > consumption.amount && consumption.amount > 0;
  const usedPct = consumption.amount > 0 ? Math.round((used / consumption.amount) * 100) : 0;
  return { used, overrun, usedPct };
}

/**
 * List-page drill-in for the progress bar: a spend summary on top, the efforts that make
 * up those figures underneath.
 */
export const PoConsumptionDetailModal: React.FC<PoConsumptionDetailModalProps> = ({
  show,
  onHide,
  po,
}) => {
  const [efforts, setEfforts] = useState<PoConsumptionEffort[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show || !po) {
      setEfforts([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    purchaseOrderService
      .getConsumptionEfforts(po.id)
      .then((rows) => {
        if (!cancelled) setEfforts(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          setEfforts([]);
          setError(err instanceof Error ? err.message : 'Failed to load efforts');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, po]);

  const consumption = po?.consumption;
  const summary = consumption && !consumption.pending ? summaryStats(consumption) : null;
  const subtitle = po ? subtitleFor(po) : '';

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <div className="tw-min-w-0">
          <Modal.Title>{po ? po.poNumber : 'Consumption'}</Modal.Title>
          {subtitle && (
            <p className="tw-mb-0 tw-mt-0.5 tw-truncate tw-text-sm tw-text-content-muted">{subtitle}</p>
          )}
        </div>
      </Modal.Header>
      <Modal.Body className="tw-flex tw-flex-col tw-gap-4">
        {consumption && !consumption.pending && summary ? (
          <section className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface-alt tw-p-4">
            <div className="tw-mb-4 tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-4">
              <Stat label="PO value" value={currency(consumption.amount)} />
              <Stat
                label="Approved"
                value={currency(consumption.approved)}
                hint="Payable"
              />
              <Stat
                label="Submitted"
                value={currency(consumption.submitted)}
                hint="Awaiting approval"
              />
              <Stat
                label={summary.overrun ? 'Over by' : 'Remaining'}
                value={currency(summary.overrun ? summary.used - consumption.amount : consumption.remaining)}
                tone={summary.overrun ? 'danger' : 'default'}
              />
            </div>
            <div className="tw-mb-2 tw-flex tw-items-baseline tw-justify-between tw-gap-3">
              <span className="tw-font-display tw-text-xs tw-font-semibold tw-tracking-wide tw-text-content-muted">
                Progress
              </span>
              <span
                className={`tw-text-sm tw-tabular-nums ${
                  summary.overrun ? 'tw-font-semibold tw-text-danger' : 'tw-text-content-secondary'
                }`}
              >
                {summary.usedPct}% used
              </span>
            </div>
            <ConsumptionBar consumption={consumption} size="lg" showLegend={false} />
          </section>
        ) : (
          <p className="tw-mb-0 tw-text-content-muted">
            Consumption is not available for this purchase order.
          </p>
        )}

        <section>
          <div className="tw-mb-2 tw-flex tw-items-baseline tw-justify-between tw-gap-3">
            <h3 className="tw-mb-0 tw-text-base tw-font-semibold tw-text-content">Efforts</h3>
            {!loading && (
              <span className="tw-text-sm tw-text-content-muted tw-tabular-nums">
                {efforts.length === 1 ? '1 line' : `${efforts.length} lines`}
              </span>
            )}
          </div>
          {error && (
            <div className="tw-mb-3 tw-text-danger" role="alert">
              {error}
            </div>
          )}
          <PoConsumptionEffortsTable efforts={efforts} loading={loading} />
        </section>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" type="button" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PoConsumptionDetailModal;
